// Typed fetch wrappers for the backend `/api/dashboard/*` surface.
//
// Each route's response shape mirrors the Pydantic model on the backend
// (see `src/kwanza/projections/customer_view.py` and
// `src/kwanza/dashboard/render/activity_sentences.py`). Adding a new
// route = adding one method here + one component that consumes it.
//
// The 401 retry policy (per F37 spec §5.6):
//   * first 401 → re-acquire the auth token, retry once
//   * second 401 → throw `SessionExpiredError` so the SPA can surface
//     the re-init prompt
//
// Cross-tenant requests are caught server-side (the verifier rejects
// `tid` mismatches with 401). The retry policy therefore covers both
// "token expired during a long-lived tab session" and "tenant config
// changed mid-session" — same code path, same user experience.

export interface CustomerView {
  tenant_slug: string;
  range_start: string;
  range_end: string;
  invoices_processed: number;
  invoices_filed_count: number;
  hours_saved: number;
  exceptions_raised_count: number;
  new_vendors_discovered: string[];
  period_label: string;
}

/** Mirrors `kwanza.dashboard.render.ActivityRow` (§13.2) — the unified
 * Atividade row. No raw framework verb / registry category leaks
 * (constraint ④): `type` is one of the seven curated `Tipo` keys, `outcome`
 * the event outcome, `needs_attention` the single canonical attention
 * signal (failure OR a flagged-but-successful action). */
export interface ActivityRow {
  timestamp: string;
  sentence: string;
  correlation_id: string;
  type: string;
  outcome: string;
  needs_attention: boolean;
}

/** Mirrors `kwanza.dashboard.render.ActivityPage`. `next_cursor` is null
 * when the window is exhausted; otherwise pass it straight back as
 * `cursor` for the next "Carregar mais" page (never interpret the value). */
export interface ActivityPage {
  items: ActivityRow[];
  next_cursor: string | null;
}

/** Tri-state Estado filter; mirrors the backend route's `estado` enum. */
export type ActivityEstado = "all" | "done" | "attention";

/** Enumerated query for `GET /api/dashboard/activity`. The SPA sends type
 * *keys* only (never an action list) — constraint ②. `since`/`until` are
 * ISO-8601 (Z); `cursor` is opaque (a prior page's `next_cursor`). */
export interface ActivityQuery {
  types?: string[];
  estado?: ActivityEstado;
  since?: string;
  until?: string;
  limit?: number;
  cursor?: string | null;
}

/** Mirrors `kwanza.qa.models.InvoiceDetail`. All fields beyond
 * `correlation_id` are nullable — a partial classification (no amount,
 * no planner task yet) still drills down. */
export interface InvoiceDetail {
  correlation_id: string;
  vendor_slug: string | null;
  vendor_display_name: string | null;
  invoice_date: string | null;
  invoice_number: string | null;
  amount: { amount: string; currency: string } | null;
  filed_at: string | null;
  sharepoint_path: string | null;
  planner_task_id: string | null;
  planner_bucket: string | null;
  classification_method: string | null;
  classification_confidence: number | null;
}

/** Mirrors `kwanza.dashboard.http.search.InvoiceDrilldown`.
 * `reasoning_trace_visible` reflects the tenant `show_reasoning_trace`
 * lever (server-side gate); when false the trace is withheld and the
 * UI shows a "hidden by policy" note instead of the steps. */
export interface InvoiceDrilldown {
  correlation_id: string;
  detail: InvoiceDetail;
  reasoning_trace: string[];
  reasoning_trace_visible: boolean;
}

/** The browsable-list status segments (Task 5). `all` = no status filter. */
export type InvoiceStatusFilter = "all" | "filed" | "review";

/** Drill / filter target when Overview navigates to the Invoices tab — a
 * Needs-you CTA or a KPI tile. `status` pre-filters the list; `correlationId`
 * opens a specific invoice's drill-in. Purely client-side navigation state
 * (no new truth source). */
export interface InvoiceTarget {
  status?: InvoiceStatusFilter;
  correlationId?: string;
}

/** Mirrors `kwanza.qa.models.InvoiceListStatus` — the per-row status of a
 * browsable-list item (never "all"; that's a filter, not a row state). */
export type InvoiceRowStatus = "filed" | "review";

/** Mirrors `kwanza.qa.models.InvoiceListItem` — one row of the browsable
 * invoice list (Task 5). Lead columns: vendor · number · date · amount ·
 * status. `correlation_id` drills into the existing detail route. */
export interface InvoiceListItem {
  correlation_id: string;
  vendor_slug: string | null;
  vendor_display_name: string | null;
  invoice_date: string | null;
  invoice_number: string | null;
  amount: { amount: string; currency: string } | null;
  filed_at: string | null;
  status: InvoiceRowStatus;
  occurred_at: string;
}

/** Mirrors `kwanza.dashboard.http.invoices.InvoiceListPage`. `next_cursor`
 * is null when the window is exhausted; otherwise pass it straight back as
 * `cursor` for the next page (never interpret the value). */
export interface InvoiceListPage {
  items: InvoiceListItem[];
  next_cursor: string | null;
}

/** Enumerated query for `GET /api/dashboard/invoices`. Typed filters only —
 * no free-text `where=` (constraint ②). `vendor` is an exact slug; the
 * vendor/number TEXT search is client-side over the page. */
export interface InvoiceListQuery {
  status?: InvoiceStatusFilter;
  vendor?: string;
  period_days?: number;
  limit?: number;
  cursor?: string | null;
}

/** Mirrors `kwanza.qa.models.ApprovalStatus`. */
export type ApprovalStatus = "pending" | "approved" | "aborted";

/** Query mode for the approvals route — `pending` cards vs `recent`
 * decisions. */
export type ApprovalQuery = "pending" | "recent";

/** Mirrors `kwanza.qa.models.ApprovalEntry`. `age_hours` is populated
 * for pending rows (how long the card has waited); `actor` carries the
 * deciding identity for terminal rows. */
export interface ApprovalEntry {
  correlation_id: string;
  status: ApprovalStatus;
  occurred_at: string;
  age_hours: number | null;
  subject: string | null;
  actor: string | null;
}

/** Mirrors `kwanza.qa.models.ExceptionEvent`. */
export interface ExceptionEvent {
  correlation_id: string;
  event_id: string;
  occurred_at: string;
  kind: string;
  detail: string | null;
  vendor_slug: string | null;
}

/** Mirrors `kwanza.dashboard.http.cost.LlmCostView`. The server decides
 * `visibility` from the tenant topology; the `$` fields are present only
 * when `visibility === "shown"` (the backend omits them otherwise — the
 * frontend never decides to hide cost). */
export interface LlmCostView {
  visibility: "shown" | "hidden";
  period_label: string;
  invoices_processed: number;
  hours_saved: number;
  advisory?: boolean;
  total_usd?: number;
  by_action?: Record<string, number>;
  calls_with_cost_error?: number;
  reason?: string;
}

/** Mirrors `kwanza.projections.sovereignty.SovereigntyAxis`. One
 * capability axis: whether the profile grants it (`enabled`) joined with
 * what the agent has done within those bounds (`count_30d` +
 * `last_activity`, null when never used). */
export interface SovereigntyAxis {
  axis_name: string;
  enabled: boolean;
  last_activity: string | null;
  count_30d: number;
}

/** Mirrors `kwanza.projections.sovereignty.SovereigntyView`. `axes` is
 * ordered to match the operator workbook's Pane 7 row order;
 * `scope_counts` carries the "scoped to N sites / M libraries / …"
 * story straight off the capability profile's scope lists. */
export interface SovereigntyView {
  tenant_slug: string;
  range_start: string;
  range_end: string;
  axes: SovereigntyAxis[];
  scope_counts: Record<string, number>;
}

/** Mirrors `kwanza.projections.agent_status.AgentHealth`. Coarse,
 * backend-decided liveness verdict; the frontend maps each to copy + a
 * dot colour and further splits `running` into active/quiet on
 * `hours_since_activity` (presentation only). */
export type AgentHealth = "running" | "idle" | "stale";

/** Mirrors `kwanza.projections.agent_status.AgentStatus`. The status
 * strip's "is it running?" payload. `version` is the backend code
 * constant (`kwanza.__version__`); the friendly product name is
 * frontend i18n copy, not a backend field. */
export interface AgentStatus {
  status: AgentHealth;
  version: string;
  last_activity_at: string | null;
  last_heartbeat_at: string | null;
  hours_since_activity: number | null;
  hours_since_heartbeat: number | null;
}

export class SessionExpiredError extends Error {
  constructor() {
    super("Teams auth token rejected twice — session expired");
    this.name = "SessionExpiredError";
  }
}

/** Thrown by `teams.getAuthToken()` when the Teams host fails to mint an
 * SSO token (e.g. `resourceDisabled`, `consentRequired`, `appNotInstalled`).
 * Distinct from `SessionExpiredError` (which is a backend 401×2): this one
 * fires BEFORE any backend request, so the dashboard surfaces `reason` as a
 * technical detail in the error panel — an SSO/manifest misconfig is then
 * diagnosable on-screen without the (hard-to-reach) tab-iframe console.
 * Added after the F37 first-load failure 2026-05-29. */
export class TeamsAuthError extends Error {
  constructor(public readonly reason: string) {
    super(`Teams SSO token acquisition failed: ${reason}`);
    this.name = "TeamsAuthError";
  }
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly route: string,
    message?: string,
  ) {
    super(message ?? `API ${route} failed: ${status}`);
    this.name = "ApiError";
  }
}

/** Dependencies injected at the App boundary so tests can substitute
 * fakes without monkeypatching modules. */
export interface ApiDeps {
  /** Per-tenant backend FQDN (e.g. `https://kj-<slug>-...`). */
  backendUrl: string;
  /** Returns a fresh AAD ID token. Called on every request + on retry
   * after a 401 — the implementation decides whether to cache. */
  getAuthToken: () => Promise<string>;
  /** Optional fetch override — defaults to the global. Tests inject
   * a mock; production uses the platform fetch. */
  fetchImpl?: typeof fetch;
}

/** One typed request → response chain. Internal helper. */
async function _request<T>(
  deps: ApiDeps,
  route: string,
  init?: RequestInit,
): Promise<T> {
  const fetchImpl = deps.fetchImpl ?? fetch;
  const url = `${deps.backendUrl.replace(/\/$/, "")}${route}`;

  const doFetch = async (): Promise<Response> => {
    const token = await deps.getAuthToken();
    return fetchImpl(url, {
      ...init,
      headers: {
        ...(init?.headers ?? {}),
        Authorization: `Bearer ${token}`,
        Accept: "application/json",
      },
    });
  };

  let response = await doFetch();
  if (response.status === 401) {
    // Re-acquire + retry once. The second 401 → SessionExpiredError.
    response = await doFetch();
    if (response.status === 401) {
      throw new SessionExpiredError();
    }
  }

  if (!response.ok) {
    throw new ApiError(response.status, route);
  }

  return (await response.json()) as T;
}

/** GET /api/dashboard/customer_view?period=<period_days> */
export async function fetchCustomerView(
  deps: ApiDeps,
  period_days = 30,
): Promise<CustomerView> {
  const route = `/api/dashboard/customer_view?period=${encodeURIComponent(
    String(period_days),
  )}`;
  return _request<CustomerView>(deps, route);
}

/** GET /api/dashboard/activity — the unified §13 Atividade feed.
 *
 * Serves both the Overview teaser (`{ limit: 5 }` — last-5 unified, no
 * filters/range) and the Atividade tab (`types` + `estado` + `since` +
 * `cursor`). Type *keys* go on the wire as repeated `types=` params
 * (constraint ②); `since`/`until` are ISO-8601 (Z); `URLSearchParams`
 * percent-encodes the `:` separators the backend then decodes. `estado=all`
 * is omitted (it's the route default) to keep the teaser URL clean. */
export async function fetchActivity(
  deps: ApiDeps,
  query: ActivityQuery = {},
): Promise<ActivityPage> {
  const params = new URLSearchParams();
  for (const key of query.types ?? []) params.append("types", key);
  if (query.estado && query.estado !== "all") params.set("estado", query.estado);
  if (query.since) params.set("since", query.since);
  if (query.until) params.set("until", query.until);
  params.set("limit", String(query.limit ?? 50));
  if (query.cursor) params.set("cursor", query.cursor);
  return _request<ActivityPage>(deps, `/api/dashboard/activity?${params.toString()}`);
}

/** GET /api/dashboard/invoices — the browsable, filterable invoice list.
 *
 * Enumerated/typed params only (status literal, exact vendor slug, period,
 * limit, opaque cursor) — constraint ②. `status=all` is omitted (the route
 * default) to keep the URL clean. The cursor is round-tripped verbatim. */
export async function fetchInvoices(
  deps: ApiDeps,
  query: InvoiceListQuery = {},
): Promise<InvoiceListPage> {
  const params = new URLSearchParams();
  if (query.status && query.status !== "all") params.set("status", query.status);
  if (query.vendor) params.set("vendor", query.vendor);
  params.set("period", String(query.period_days ?? 30));
  params.set("limit", String(query.limit ?? 50));
  if (query.cursor) params.set("cursor", query.cursor);
  return _request<InvoiceListPage>(
    deps,
    `/api/dashboard/invoices?${params.toString()}`,
  );
}

/** GET /api/dashboard/invoice/{correlation_id}
 *
 * The id is path-segment-encoded so a slug with characters the URL
 * grammar reserves can't break the route; the backend still validates
 * it against a bounded regex (malformed → 422, unknown → 404). The
 * caller surfaces a friendly "not found" on the 404 `ApiError`. */
export async function fetchInvoiceDetail(
  deps: ApiDeps,
  correlationId: string,
): Promise<InvoiceDrilldown> {
  const route = `/api/dashboard/invoice/${encodeURIComponent(correlationId)}`;
  return _request<InvoiceDrilldown>(deps, route);
}

/** GET /api/dashboard/approvals?status=<pending|recent>&limit=<limit> */
export async function fetchApprovals(
  deps: ApiDeps,
  status: ApprovalQuery = "pending",
  limit = 50,
): Promise<ApprovalEntry[]> {
  const route =
    `/api/dashboard/approvals?status=${encodeURIComponent(status)}` +
    `&limit=${encodeURIComponent(String(limit))}`;
  return _request<ApprovalEntry[]>(deps, route);
}

/** GET /api/dashboard/exceptions?limit=<limit> */
export async function fetchExceptions(
  deps: ApiDeps,
  limit = 50,
): Promise<ExceptionEvent[]> {
  const route = `/api/dashboard/exceptions?limit=${encodeURIComponent(
    String(limit),
  )}`;
  return _request<ExceptionEvent[]>(deps, route);
}

/** GET /api/dashboard/cost/llm?period=<period_days> */
export async function fetchLlmCost(
  deps: ApiDeps,
  period_days = 30,
): Promise<LlmCostView> {
  const route = `/api/dashboard/cost/llm?period=${encodeURIComponent(
    String(period_days),
  )}`;
  return _request<LlmCostView>(deps, route);
}

/** GET /api/dashboard/sovereignty?period=<period_days> */
export async function fetchSovereignty(
  deps: ApiDeps,
  period_days = 30,
): Promise<SovereigntyView> {
  const route = `/api/dashboard/sovereignty?period=${encodeURIComponent(
    String(period_days),
  )}`;
  return _request<SovereigntyView>(deps, route);
}

/** GET /api/dashboard/status — agent liveness + version for the strip. */
export async function fetchStatus(deps: ApiDeps): Promise<AgentStatus> {
  return _request<AgentStatus>(deps, "/api/dashboard/status");
}
